import {
  ErrorHandler,
  Inject,
  Injectable,
  InjectionToken,
} from '@angular/core';
import * as Sentry from '@sentry/angular';

export const SENTRY_ERROR_HANDLER = new InjectionToken<ErrorHandler>(
  'SentryErrorHandler',
  {
    providedIn: 'root',
    factory: () => Sentry.createErrorHandler({ logErrors: true }),
  },
);

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    @Inject(SENTRY_ERROR_HANDLER) private sentryErrorHandler: ErrorHandler,
  ) {
    try {
      sessionStorage.removeItem('fc-chunk-load-failed-reloaded');
    } catch (e) {
      console.warn('Unable to clear chunk reload flag from sessionStorage', e);
    }
  }

  handleError(error: any): void {
    const chunkFailedMessage =
      /Failed to fetch dynamically imported module|Importing a module script failed/i;

    // Basic extraction just to catch chunk load errors before delegating to Sentry
    let extractedError = error;
    if (error && error.rejection) {
      extractedError = error.rejection;
    } else if (error && error.originalError) {
      extractedError = error.originalError;
    }

    const errorMessage =
      typeof extractedError === 'string'
        ? extractedError
        : extractedError?.message || '';

    if (chunkFailedMessage.test(errorMessage)) {
      let alreadyReloaded = false;
      const storageKey = 'fc-chunk-load-failed-reloaded';

      try {
        alreadyReloaded = sessionStorage.getItem(storageKey) === 'true';
      } catch (e) {
        console.warn('Unable to read from sessionStorage', e);
      }

      if (!alreadyReloaded) {
        try {
          sessionStorage.setItem(storageKey, 'true');
        } catch (e) {
          console.warn('Unable to write to sessionStorage', e);
        }

        console.error(
          'Chunk load error detected, reloading page with cache bust...',
          extractedError,
        );

        try {
          const url = new URL(window.location.href);
          url.searchParams.set('cb', Date.now().toString());
          window.location.href = url.toString();
        } catch (e) {
          window.location.reload();
        }
        return;
      } else {
        console.warn(
          'Chunk load error detected after reload, delegating to Sentry to prevent infinite loop.',
        );
      }
    }

    // Delegate to Sentry's built-in Angular ErrorHandler which handles
    // HttpErrorResponse, Promise rejections, ErrorEvent, etc. natively.
    this.sentryErrorHandler.handleError(error);
  }
}
